import React, { useState,   useCallback, useMemo } from "react"
import { Button, Layout, Typography, Row, Col, Empty, Modal, message } from "antd"
import { SearchOutlined, EnvironmentOutlined, TrophyOutlined, ProfileOutlined, SafetyCertificateOutlined, SettingOutlined, FilterOutlined, ExternalLinkOutlined } from '@ant-design/icons';
import { Formik, Form } from "formik"
import { useHistory, useLocation } from "react-router-dom"

import { validationSchema } from "./JobBoardSearchForm"

import * as JobBoardFormFields from "./JobBoardFormFields"

import { useJobBoardSearch, useSearchParams, useSearchResults } from "../context/searchContext"
import monsterAdapter from "../adapters/monsterAdapter"
import { useSearchHistory } from "../hooks/useSearchHistory"

import { useSearchFunction } from "../utils/useSearchFuntion"
import {  MONSTER_CANDIDATE_LIST_V2 } from "../../../../../ApiUrl"
import SearchLanding from "./MonsterSearchPage"
import MonsterSearchResults from "./MonsterSearchResults"
import cssStyles from './Monster.module.css'
import ResizableSidebar from "../components/ResizableSidebar"
import SearchFilters from './SearchFilters'
import CandidateDetailView from "./CandidateDetailView"
import CompareView from "./CompareView"
import {
  getJobBoardBasePath,
  constructSearchPath,
  constructCandidatePath,
  constructComparePath,
  parseSearchParams,
  parseCompareParams
} from '../utils/routeUtils';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import DetailsSkeleton from '../components/DetailsSkeleton';
import { NotesProvider } from '../context/notesContext';

const { Title, Text } = Typography

export const initialFields = [
  { name: "keyword", label: "Keyword", type: "KeywordField", props: { allowClear: true, autoFocus: true } },
  { name: "location", label: "Location", type: "LocationField" },
  {
    name: "radius",
    label: "Radius",
    type: "SelectField",
    props: {
      options: [
        { value: "10", label: "10 miles" },
        { value: "20", label: "20 miles" },
        { value: "30", label: "30 miles" },
        { value: "50", label: "50 miles" },
      ],
      defaultValue: "30"
    },
  },
]

export const advancedFields = [
  { name: "jobTitle", label: "Job Title", type: "JobTitleField" },
  { name: "targetJobTitle", label: "Target Job Title", type: "JobTitleField" },
  { name: "recentCompany", label: "Recent Company", type: "TextField" },
  { name: "schoolName", label: "School Name", type: "TextField" },
  { name: "recentJobDescription", label: "Recent Job Description", type: "TextField" },
  {
    name: "keywordBehavior",
    label: "Keyword Search Type",
    type: "SelectField",
    props: {
      options: [
        { value: "And", label: "Match All Words (AND)" },
        { value: "Or", label: "Match Any Word (OR)" },
        { value: "Bool", label: "Boolean Query" },
      ],
    },
  },
  {
    name: "postalCode",
    label: "Postal Code",
    type: "PostalCodeField",
    props: {
      placeholder: "Enter ZIP/Postal code"
    }
  },
  // {
  //   name: "radius",
  //   label: "Search Radius",
  //   type: "SelectField",
  //   props: {
  //     options: [
  //       { value: "10", label: "10 miles" },
  //       { value: "20", label: "20 miles" },
  //       { value: "30", label: "30 miles" },
  //       { value: "50", label: "50 miles" },
  //       { value: "100", label: "100 miles" },
  //     ],
  //   },
  // },
  {
    name: "targetWorkLocation",
    label: "Target Work Location",
    type: "LocationField",
  },
  {
    name: "targetWorkLocationBoolean",
    label: "Target Location Logic",
    type: "SelectField",
    props: {
      options: [
        { value: "0", label: "Match Either Location (OR)" },
        { value: "1", label: "Match Both Locations (AND)" },
      ],
    },
  },
  {
    name: "targetWorkSite",
    label: "Target Work Site",
    type: "SelectField",
    props: {
      options: [
        { value: "1", label: "On-site" },
        { value: "2", label: "Off-site" },
        { value: "3", label: "No Preference" },
      ],
    },
  },
  {
    name: "experience",
    label: "Years of Experience",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "Less than 1 Year" },
        { value: "2", label: "1+ to 2 Years" },
        { value: "3", label: "2+ to 5 Years" },
        { value: "4", label: "5+ to 7 Years" },
        { value: "5", label: "7+ to 10 Years" },
        { value: "6", label: "10+ to 15 Years" },
        { value: "7", label: "More than 15 Years" },
      ],
    },
  },
  {
    name: "militaryStatus",
    label: "Military Status",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "0", label: "None" },
        { value: "1", label: "Unspecified" },
        { value: "2", label: "Active Duty" },
        { value: "3", label: "Retired Military" },
        { value: "4", label: "Veteran/Prior Service" },
        { value: "5", label: "Reservist (drilling)" },
        { value: "6", label: "National Guard" },
        { value: "7", label: "Inactive Reserve/Guard (not drilling)" },
        { value: "8", label: "Service Academy" },
        { value: "9", label: "In Military Preparation" },
        { value: "10", label: "Other Military Program" },
        { value: "11", label: "Government Employee" },
        { value: "12", label: "Defense Contractor" },
        { value: "13", label: "Considering Joining" },
        { value: "14", label: "Military Spouse" },
        { value: "15", label: "Spouse of a Veteran" },
        { value: "16", label: "Other Military Family Member" },
      ],
    },
  },
  {
    name: "industry",
    label: "Current Industry",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "1", label: "Agriculture/Forestry/Fishing" },
        { value: "2", label: "Metals and Minerals" },
        { value: "3", label: "Energy and Utilities" },
        { value: "4", label: "Construction - Industrial Facilities and Infrastructure" },
        { value: "5", label: "Trade Contractors" },
        { value: "6", label: "Aerospace and Defense" },
        { value: "7", label: "Automotive and Parts Mfg" },
        { value: "8", label: "Biotechnology/Pharmaceuticals" },
        { value: "9", label: "Chemicals/Petro-Chemicals" },
        { value: "10", label: "Consumer Packaged Goods Manufacturing" },
        { value: "11", label: "Electronics, Components, and Semiconductor Mfg" },
        { value: "12", label: "Manufacturing - Other" },
        { value: "13", label: "Printing and Publishing" },
        { value: "14", label: "Clothing and Textile Manufacturing" },
        { value: "15", label: "Wholesale Trade/Import-Export" },
        { value: "16", label: "Grocery/Convenience/Gas" },
        { value: "17", label: "Retail" },
        { value: "18", label: "Travel, Transportation and Tourism" },
        { value: "19", label: "Transport and Storage - Materials" },
        { value: "20", label: "Internet Services" },
        { value: "21", label: "Broadcasting, Music, and Film" },
        { value: "22", label: "Telecommunications Services" },
        { value: "23", label: "Banking" },
        { value: "24", label: "Insurance" },
        { value: "25", label: "Mortgage" },
        { value: "26", label: "Real Estate/Property Management" },
        { value: "27", label: "Rental Services" },
        { value: "28", label: "Accounting and Auditing Services" },
        { value: "29", label: "Advertising and PR Services" },
        { value: "30", label: "Architectural and Design Services" },
        { value: "31", label: "Management Consulting Services" },
        { value: "32", label: "Computer Hardware" },
        { value: "33", label: "Computer Software" },
        { value: "34", label: "Legal Services" },
        { value: "35", label: "Management and Holding Companies" },
        { value: "36", label: "Administrative and Support Services" },
        { value: "37", label: "Waste Management" },
        { value: "38", label: "Education" },
        { value: "39", label: "Healthcare Services" },
        { value: "40", label: "Nursing/Residential Care Facilities" },
        { value: "41", label: "Social Services" },
        { value: "42", label: "Performing and Fine Arts" },
        { value: "43", label: "Sports and Physical Recreation" },
        { value: "44", label: "Hotels and Lodging" },
        { value: "45", label: "Restaurant/Food Services" },
        { value: "46", label: "Staffing/Employment Agencies" },
        { value: "47", label: "Nonprofit Charitable Organizations" },
        { value: "48", label: "Personal and Household Services" },
        { value: "49", label: "Repair and Maintenance Services" },
        { value: "50", label: "Government and Military" },
        { value: "51", label: "Government- Military/Defense" },
        { value: "52", label: "Government- National" },
        { value: "74", label: "Security and Surveillance" },
        { value: "75", label: "Automotive Sales and Repair Services" },
        { value: "76", label: "Business Services - Other" },
        { value: "77", label: "Computer/IT Services" },
        { value: "78", label: "Construction - Residential & Commercial/Office" },
        { value: "79", label: "Engineering Services" },
        { value: "80", label: "Entertainment Venues and Theaters" },
        { value: "81", label: "Financial Services" },
        { value: "82", label: "Food and Beverage Production" },
        { value: "83", label: "Marine Mfg & Services" },
        { value: "84", label: "Medical Devices and Supplies" },
        { value: "85", label: "Other/Not Classified" }
      ],
    },
  },
  {
    name: "languageSkills",
    label: "Language Skills",
    type: "LanguageSkillField",
    props: {
      proficiencyLevels: [
        { value: "0", label: "Unknown" },
        { value: "1", label: "Basic – Familiar" },
        { value: "2", label: "Conversational - Limited" },
        { value: "3", label: "Conversational" },
        { value: "4", label: "Conversational – Advanced" },
        { value: "5", label: "Fluent - Wide Knowledge" },
        { value: "6", label: "Fluent - Full Knowledge" }
      ]
    }
  },
  {
    name: "resumeLanguage",
    label: "Resume Language",
    type: "SelectField",
    props: {
      options: [
        { value: "en", label: "English" },
        { value: "es", label: "Spanish" },
        { value: "fr", label: "French" },
        // Note: We need the complete list of language codes from Monster API
      ]
    }
  },
  { name: "educationMajor", label: "Education Major", type: "TextField" },
  {
    name: "education",
    label: "Education",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "1", label: "High School or equivalent" },
        { value: "2", label: "Certification" },
        { value: "3", label: "Vocational" },
        { value: "4", label: "Associate Degree" },
        { value: "5", label: "Bachelor's Degree" },
        { value: "6", label: "Master's Degree" },
        { value: "7", label: "Doctorate" },
        { value: "8", label: "Professional" },
        { value: "9", label: "Some College Coursework" },
        { value: "10", label: "Vocational - High School" },
        { value: "11", label: "Vocational - Degree" },
        { value: "12", label: "Some High School Coursework" },
      ],
    },
  },
  {
    name: "jobType",
    label: "Job Type",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "Full Time", label: "Full Time" },
        { value: "Part Time", label: "Part Time" },
        { value: "Contract", label: "Contract" },
        { value: "Intern", label: "Intern" },
        { value: "Per Diem", label: "Per Diem" },
        { value: "Seasonal", label: "Seasonal" },
      ],
    },
  },
  {
    name: "careerLevel",
    label: "Career Level",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "10", label: "Student (Undergraduate/Graduate)" },
        { value: "11", label: "Entry Level" },
        { value: "12", label: "Experienced (Non-Manager)" },
        { value: "13", label: "Manager (Manager/Supervisor of Staff)" },
        { value: "14", label: "Executive (SVP, VP, Department Head, etc)" },
        { value: "15", label: "Senior Executive (President, CFO, etc)" },
        { value: "16", label: "Student (High School)" },
      ],
    },
  },
  {
    name: "workAuthorization",
    label: "Work Authorization",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "1", label: "Authorized to work for any employer" },
        { value: "2", label: "Authorized to work for current employer only" },
        { value: "3", label: "Require sponsorship" },
      ],
    },
  },
  {
    name: "salary",
    label: "Target Salary",
    type: "SalaryRangeField",
    props: {
      currencies: [
        { value: "USD", label: "USD" },
        { value: "EUR", label: "EUR" },
        { value: "GBP", label: "GBP" },
      ],
      types: [
        { value: "1", label: "Per year" },
        { value: "2", label: "Per hour" },
        { value: "3", label: "Per week" },
        { value: "4", label: "Per month" },
      ],
      includeNormalized: true,
      includeSalaryNotSpecified: true,
    },
  },
  { name: "willingToRelocate", label: "Willing to Relocate", type: "CheckboxField" },
  {
    name: "updatedWithin",
    label: "Updated Within",
    type: "SelectField",
    props: {
      options: [
        { value: "1440", label: "Last 24 hours" },
        { value: "10080", label: "Last 7 days" },
        { value: "43200", label: "Last 30 days" },
        { value: "129600", label: "Last 90 days" },
      ],
    },
  },
  {
    name: "securityClearance",
    label: "Security Clearance",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "12", label: "Active Confidential" },
        { value: "13", label: "Active Secret" },
        { value: "14", label: "Active Top Secret" },
        { value: "15", label: "Inactive Confidential" },
        { value: "16", label: "Inactive Secret" },
        { value: "17", label: "Inactive Top Secret" },
      ],
    },
  },
  { name: "managerialExperience", label: "Has Managerial Experience", type: "CheckboxField" },
  { name: "projectLeadership", label: "Has Project Leadership Experience", type: "CheckboxField" },
  {
    name: "sortBy",
    label: "Sort Results By",
    type: "SelectField",
    props: {
      options: [
        { value: "rank", label: "Relevance" },
        { value: "mdate", label: "Last Modified" },
        { value: "cdate", label: "Creation Date" },
        { value: "distance", label: "Distance" },
        { value: "title", label: "Title" },
      ],
    },
  },
]

const useStyles = {
  pageLayout: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  contentLayout: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  sidebarLayout: {
    backgroundColor: '#fff',
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
    zIndex: 1,
  },
  sidebarContent: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  mainContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  mainHeader: {
    padding: '16px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
    zIndex: 1,
  },
  mainResults: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  }
}

const modalStyles = {
  content: {
    padding: '0',
    borderRadius: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '90vh',
    backgroundColor: '#f5f6fa',
  },
  header: {
    padding: '24px 32px',
    background: 'linear-gradient(135deg, #4834d4 0%, #686de0 100%)',
    color: 'white',
    marginBottom: 0,
    position: 'relative',
    zIndex: 1,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  },
  title: {
    color: 'white',
    fontSize: '28px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  description: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '16px',
  },
  formContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    backgroundColor: '#f5f6fa',
  },
  formSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#4834d4',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
    alignItems: 'start',
  },
  footer: {
    padding: '16px 32px',
    background: 'white',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
};

const formSections = [
  {
    title: "Basic Search",
    icon: <SearchOutlined />,
    fields: ["keyword", "keywordBehavior", "jobTitle", "targetJobTitle", "recentCompany", "recentJobDescription"]
  },
  {
    title: "Location & Work Site",
    icon: <EnvironmentOutlined />,
    fields: ["location", "radius", "postalCode", "targetWorkLocation", "targetWorkLocationBoolean", "targetWorkSite", "willingToRelocate"]
  },
  {
    title: "Experience & Education",
    icon: <TrophyOutlined />,
    fields: ["experience", "education", "educationMajor", "careerLevel", "managerialExperience", "projectLeadership"]
  },
  {
    title: "Job Details",
    icon: <ProfileOutlined />,
    fields: ["jobType", "industry", "targetIndustry", "salary"]
  },
  {
    title: "Additional Qualifications",
    icon: <SafetyCertificateOutlined />,
    fields: ["languageSkills", "resumeLanguage", "workAuthorization", "securityClearance", "militaryStatus"]
  },
  {
    title: "Search Options",
    icon: <SettingOutlined />,
    fields: ["updatedWithin", "sortBy"]
  }
];

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }
};

const MonsterSearch = React.memo(() => {
  // Memoize static data
  const initialFieldsData = useMemo(() => initialFields, []);
  const formSectionsData = useMemo(() => formSections.map(section => ({
    ...section,
    icon: React.cloneElement(section.icon, {
      style: {
        color: '#4834d4',
        fontSize: '18px',
        marginRight: '8px'
      }
    })
  })), []);
  
  // Use individual hooks for better performance
  const { searchParams, loading: searchLoading, error: searchError } = useSearchParams();
  const { results, totalResults } = useSearchResults();
  const { dispatch } = useJobBoardSearch();
  const history = useHistory();
  const location = useLocation();

  const [localState, setLocalState] = useState({
    showAdvancedFilters: false,
    sidebarCollapsed: false,
    searchType: "boolean",
    searchSuggestions: [],
    isAdvanceOptionsOpen: false,
    currentSearchValues: {}
  });

  const updateLocalState = useCallback((updates) => {
    if (typeof updates === 'function') {
      setLocalState(prev => ({...prev, ...updates(prev)}));
    } else {
      setLocalState(prev => ({...prev, ...updates}));
    }
  }, []);

  const { search } = useSearchFunction(MONSTER_CANDIDATE_LIST_V2);
  const { addRecentSearch } = useSearchHistory();

  // Replace getBasePath with the utility version
  const getBasePath = useCallback(() => {
    return getJobBoardBasePath(location.pathname, 'monsterV2');
  }, [location.pathname]);

  // Update handleSubmit
  const handleSubmit = useCallback(async (values, page = 1) => {
    try {
      // Ensure values is an object
      values = values || {};
      
      // If values is a string, assume it's a keyword search
      if (typeof values === 'string') {
        values = { keyword: values };
      }
      
      // Check various potential sources for search criteria
      // 1. First check the values directly provided to this function
      let hasSearchCriteria = !!(values.keyword || values.jobTitle || values.targetJobTitle);
      
      // 2. Check the currentSearchValues in localState
      if (!hasSearchCriteria && localState.currentSearchValues) {
        const { keyword, jobTitle, targetJobTitle } = localState.currentSearchValues;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        // Merge in values from currentSearchValues if needed
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      // 3. Check the searchParams from context
      if (!hasSearchCriteria && searchParams) {
        const { keyword, jobTitle, targetJobTitle } = searchParams;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        // Merge in values from searchParams if needed
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      // 4. Check initialFormValues as a last resort
      if (!hasSearchCriteria && initialFormValues) {
        const { keyword, jobTitle, targetJobTitle } = initialFormValues;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        // Merge in values from initialFormValues if needed
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      // Now validate with our best effort to find search criteria
      if (!hasSearchCriteria) {
        console.error('No search criteria found in any source:', {
          values,
          currentSearchValues: localState.currentSearchValues,
          searchParams,
          initialFormValues
        });
        message.error('Please enter at least a keyword or job title to search');
        return;
      }
      
      // Ensure keyword is a string
      if (values.keyword === undefined || values.keyword === null) {
        values.keyword = '';
      }
      
      // Set default radius if not specified
      if (!values.radius) {
        values.radius = "30";
      }
      
      // Store the current values for future reference
      updateLocalState(prev => ({
        ...prev,
        currentSearchValues: {
          ...prev.currentSearchValues,
          ...values
        }
      }));
      
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      const mergedValues = {
        ...searchParams,
        ...values,
        page: Number(page),
        pageSize: 20,
        radius: values.radius || "30" // Ensure radius is set
      };

      const basePath = getBasePath();
      const searchPath = constructSearchPath(basePath, mergedValues);
      history.push(searchPath);

      const searchResults = await search(mergedValues, monsterAdapter);
      
      // searchResults will always be returned now, even if empty
      // The search function now returns { results: [], total: 0, error: message } on error
      
      if (searchResults.error) {
        throw new Error(searchResults.error);
      }

      if (!searchResults.results || searchResults.results.length === 0) {
        message.info('No candidates found matching your search criteria');
      }

      dispatch({
        type: "SET_RESULTS",
        payload: { 
          results: searchResults?.results || [],
          total: searchResults?.total || 0
        },
      });
      dispatch({ type: "SET_SEARCH_PARAMS", payload: mergedValues });

      if (searchResults?.total !== undefined && values.keyword) {
        const searchData = {
          title: values.keyword,
          query: mergedValues,
          matches: searchResults.total
        };

        try {
          await addRecentSearch(searchData);
          updateLocalState(prev => ({
            searchSuggestions: [searchData, ...prev.searchSuggestions].slice(0, 5)
          }));
        } catch (error) {
          console.error('[MonsterSearch] Failed to save search history:', error);
          message.error('Failed to save search history');
        }
      }
    } catch (error) {
      console.error('[MonsterSearch] Search error:', error);
      dispatch({ type: "SET_ERROR", payload: error.message });
      dispatch({
        type: "SET_RESULTS",
        payload: { 
          results: [],
          total: 0
        },
      });
      message.error('Search failed: ' + error.message);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [searchParams, search, dispatch, addRecentSearch, updateLocalState, history, getBasePath]);

  // Update candidate selection handler
  const handleCandidateSelect = useCallback((candidate) => {
    const basePath = getBasePath();
    const candidatePath = constructCandidatePath(basePath, candidate.id);
    history.push(candidatePath);
  }, [history, getBasePath]);

  // Update compare view handler
  const handleCompareView = useCallback((candidates) => {
    const basePath = getBasePath();
    const comparePath = constructComparePath(basePath, candidates.map(c => c.id));
    history.push(comparePath);
  }, [history, getBasePath]);

  // Add handler for external link
  const handleExternalView = useCallback((candidate) => {
    const basePath = getBasePath();
    const candidatePath = constructCandidatePath(basePath, candidate.id);
    window.open(candidatePath, '_blank');
  }, [getBasePath]);

  // Update form values initialization
  const initialFormValues = useMemo(() => {
    if (location.search) {
      return parseSearchParams(location.search);
    }
    return searchParams || {};
  }, [location.search, searchParams]);

  // Get the current route path
  const { pathname } = location;
  const isResultsView = pathname.includes('/results');
  const isCompareView = pathname.includes('/compare');
  const candidateId = pathname.includes('/candidate/') ? pathname.split('/').pop() : null;

  // Update renderContent
  const renderContent = () => {
    if (isCompareView) {
      const candidateIds = parseCompareParams(location.search);
      const selectedCandidates = results.filter(c => candidateIds.includes(c.id));
      
      return (
        <Layout style={useStyles.contentLayout}>
          <CompareView
            candidates={selectedCandidates}
            onClose={() => {
              const basePath = getBasePath();
              history.push(`${basePath}/results${location.search}`);
            }}
            onViewCandidate={(candidate) => {
              const basePath = getBasePath();
              const candidatePath = constructCandidatePath(basePath, candidate.id);
              history.push(candidatePath);
            }}
            onRemoveFromCompare={(candidateId) => {
              const remainingIds = selectedCandidates
                .filter(c => c.id !== candidateId)
                .map(c => c.id);
              
              if (remainingIds.length < 2) {
                const basePath = getBasePath();
                history.push(`${basePath}/results${location.search}`);
              } else {
                const basePath = getBasePath();
                const comparePath = constructComparePath(basePath, remainingIds);
                history.push(comparePath);
              }
            }}
          />
        </Layout>
      );
    }

    if (isResultsView) {
      return (
        <Layout style={useStyles.contentLayout}>
          <ResizableSidebar
            collapsed={localState.sidebarCollapsed}
            onCollapse={() => updateLocalState({ sidebarCollapsed: !localState.sidebarCollapsed })}
            width={300}
            collapsedWidth={80}
          >
            <SearchFilters
              searchParams={searchParams}
              onSubmit={handleSubmit}
              isInSidebar={true}
            />
          </ResizableSidebar>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={55} minSize={40} maxSize={65}>
              <MonsterSearchResults
                results={results}
                loading={searchLoading}
                error={searchError}
                searchQuery={searchParams}
                totalResults={totalResults}
                onPageChange={(page, pageSize) => handleSubmit(searchParams, page)}
                onCandidateSelect={handleCandidateSelect}
                onCompareView={handleCompareView}
                onExternalView={handleExternalView}
              />
            </Panel>

            <PanelResizeHandle>
              <div style={{
                width: '4px',
                backgroundColor: '#f0f0f0',
                cursor: 'col-resize',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#d9d9d9'
                }
              }} />
            </PanelResizeHandle>

            <Panel defaultSize={45} minSize={35} maxSize={60}>
              <Layout style={{ height: '100%', backgroundColor: '#fff' }}>
                {searchLoading ? (
                  <DetailsSkeleton />
                ) : candidateId ? (
                  <CandidateDetailView
                    candidate={results.find(c => c.id === candidateId)}
                    onNext={() => {
                      const currentIndex = results.findIndex((c) => c.id === candidateId);
                      if (currentIndex < results.length - 1) {
                        handleCandidateSelect(results[currentIndex + 1]);
                      }
                    }}
                    onPrevious={() => {
                      const currentIndex = results.findIndex((c) => c.id === candidateId);
                      if (currentIndex > 0) {
                        handleCandidateSelect(results[currentIndex - 1]);
                      }
                    }}
                    hasNext={results.findIndex((c) => c.id === candidateId) < results.length - 1}
                    hasPrevious={results.findIndex((c) => c.id === candidateId) > 0}
                  />
                ) : (
                  <Empty
                    description="Select a candidate to view details"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Layout>
            </Panel>
          </PanelGroup>
        </Layout>
      );
    }

    // Default to landing page
    return (
      <SearchLanding
        searchType={localState.searchType}
        onSearchTypeChange={(type) => updateLocalState({ searchType: type })}
        initialFields={localState.searchType === "boolean" ? initialFieldsData : [
          { name: "jobTitle", label: "Job Title", type: "TextField" },
          ...initialFieldsData.slice(1)
        ]}
        onSubmit={(values) => {
          // Store the current form values when submitting from main search
          updateLocalState({ currentSearchValues: values });
          handleSubmit(values);
        }}
        searchParams={initialFormValues}
        handleOpenAdvanceOptions={(formValues) => {
          // Get and preserve the current main form values before opening modal
          
          // Use formValues if provided, otherwise use initialFormValues
          const valuesToUse = formValues || 
            (Object.keys(localState.currentSearchValues).length > 0 
              ? localState.currentSearchValues 
              : initialFormValues);
          
          // Update both the search params and our local tracking of current values
          dispatch({ type: "SET_SEARCH_PARAMS", payload: valuesToUse });
          updateLocalState({ 
            currentSearchValues: valuesToUse,
            isAdvanceOptionsOpen: true 
          });
        }}
        searchSuggestions={localState.searchSuggestions}
      />
    );
  };

  const modalContent = useMemo(() => (
    <div style={{
      ...modalStyles.content,
      backgroundColor: '#f5f6fa',
      borderRadius: '16px',
      overflow: 'hidden'
    }}>
      <div style={{
        ...modalStyles.header,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px'
      }}>
        <Title level={3} style={modalStyles.title}>Advanced Search Options</Title>
        <Text style={modalStyles.description}>
          Refine your search with additional filters to find the most relevant candidates
        </Text>
      </div>

      <div style={{
        ...modalStyles.formContainer,
        backgroundColor: '#f5f6fa'
      }}>
        <Formik
          initialValues={(() => {
            // Gather all possible sources of values and merge them in priority order
            const values = {};
            
            // Start with any searchParams from context
            if (searchParams) {
              Object.assign(values, searchParams);
            }
            
            // Add any values from the current localState (higher priority)
            if (localState.currentSearchValues) {
              Object.assign(values, localState.currentSearchValues);
            }
            
            // Extract and ensure we have the essential search fields from all sources
            const keyword = values.keyword || searchParams?.keyword || localState.currentSearchValues?.keyword || '';
            const jobTitle = values.jobTitle || searchParams?.jobTitle || localState.currentSearchValues?.jobTitle || '';
            const targetJobTitle = values.targetJobTitle || searchParams?.targetJobTitle || localState.currentSearchValues?.targetJobTitle || '';
            
            // Explicitly ensure these fields are set
            values.keyword = keyword;
            values.jobTitle = jobTitle;
            values.targetJobTitle = targetJobTitle;
            
            return values;
          })()}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            // Explicitly preserve keyword, jobTitle, and targetJobTitle from current search params
            const mergedValues = { ...values };
            
            // Preserve keyword from initial search if not in advanced form
            if (!mergedValues.keyword && localState.currentSearchValues.keyword) {
              mergedValues.keyword = localState.currentSearchValues.keyword;
            }
            
            // Preserve jobTitle from initial search if not in advanced form
            if (!mergedValues.jobTitle && localState.currentSearchValues.jobTitle) {
              mergedValues.jobTitle = localState.currentSearchValues.jobTitle;
            }
            
            // Preserve targetJobTitle from initial search if not in advanced form
            if (!mergedValues.targetJobTitle && localState.currentSearchValues.targetJobTitle) {
              mergedValues.targetJobTitle = localState.currentSearchValues.targetJobTitle;
            }
            
            handleSubmit(mergedValues);
            updateLocalState({ isAdvanceOptionsOpen: false });
          }}
          enableReinitialize={true}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            return (
              <Form layout="vertical">
                {/* Debug component to show current search criteria */}
                <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                  <Text strong>Current Search Criteria:</Text>
                  {values.keyword && (
                    <div><Text type="secondary">Keyword:</Text> {values.keyword}</div>
                  )}
                  {values.jobTitle && (
                    <div><Text type="secondary">Job Title:</Text> {values.jobTitle}</div>
                  )}
                  {values.targetJobTitle && (
                    <div><Text type="secondary">Target Job Title:</Text> {values.targetJobTitle}</div>
                  )}
                  {!values.keyword && !values.jobTitle && !values.targetJobTitle && (
                    <div><Text type="danger">No search criteria defined!</Text></div>
                  )}
                </div>
                
                {formSectionsData.map((section, index) => (
                  <div key={index} style={modalStyles.formSection} className={cssStyles.modalFormSection}>
                    <div style={modalStyles.sectionTitle} className={cssStyles.modalSectionTitle}>
                      {section.icon}
                      {section.title}
                    </div>
                    <div style={modalStyles.fieldsGrid} className={cssStyles.modalFieldsGrid}>
                      {section.fields.map(fieldName => {
                        const field = advancedFields.find(f => f.name === fieldName);
                        if (!field) return null;
                        const FieldComponent = JobBoardFormFields[field.type];
                        if (!FieldComponent) {
                          console.warn(`Field type "${field.type}" not found for field "${field.name}"`);
                          return null;
                        }
                        return (
                          <div key={field.name} className={cssStyles.modalFormItem}>
                            <FieldComponent
                              name={field.name}
                              label={field.label}
                              {...field.props}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div style={{
                  ...modalStyles.footer,
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px'
                }} className={cssStyles.modalFooter}>
                  <Button 
                    onClick={() => updateLocalState({ isAdvanceOptionsOpen: false })}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    icon={<FilterOutlined />}
                    size="large"
                  >
                    Apply Filters
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  ), [searchParams, handleSubmit, formSectionsData, modalStyles, localState.currentSearchValues]);

  return (
    <NotesProvider>
      <Layout style={useStyles.pageLayout} className={cssStyles.monsterSearchContainer}>
        {renderContent()}
        <Modal
          title={null}
          open={localState.isAdvanceOptionsOpen}
          onCancel={() => updateLocalState({ isAdvanceOptionsOpen: false })}
          maskClosable={false}
          keyboard={true}
          destroyOnClose={true}
          footer={null}
          width={1200}
          centered
          className={cssStyles.advancedModal}
          bodyStyle={{ 
            padding: 0, 
            borderRadius: '16px', 
            backgroundColor: '#f5f6fa',
            overflow: 'hidden'
          }}
          style={{ top: 20 }}
          maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
          wrapClassName={cssStyles.advancedModal}
        >
          {modalContent}
        </Modal>
      </Layout>
    </NotesProvider>
  );
});

MonsterSearch.displayName = 'MonsterSearch';

export default MonsterSearch;

